import React, { useEffect, useMemo, useState } from 'react';
import { BackButton, TabBar, SkeletonHero, SkeletonCard, SectionError } from './ui';
import PlayerHero from './PlayerHero';
import {
  PlayerInfoCard, RecentStatsCard, HonoursCard, CurrentClubCard,
  TransferHistoryCard, RecentMatchesCard, MatchAppearancesTable, CareerTotalsCard, SquadInfoCard,
  SeasonStatDetail, SeasonStatsTable,
} from './playerCards';
import StandingsTable from './StandingsTable';
import SeasonSelect from './SeasonSelect';
import PlayerReport from './report/PlayerReport';
import { normalizePlayer, pickDefaultSeason, sortSeasonsDesc, seasonShortLabel } from '../../lib/playerFormatters';

const TABS = ['Overview', 'Season Stats', 'Matches', 'Career & Trophies', 'Squad', 'Standings', 'Report'];
const EP = (id) => `https://stats.interaios.com/api/v1/players-sportsmonk/get-player/${id}`;
const TEAM_EP = (id) => `https://stats.interaios.com/api/v1/teams-sportsmonk/get-team/${id}`;

export default function PlayerIntelligence({ playerId, showBack = true, className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [clubStandings, setClubStandings] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!playerId) { setLoading(false); setData(null); setError(null); return undefined; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(EP(playerId));
        if (!res.ok) throw new Error(`Player data unavailable (${res.status})`);
        const json = await res.json();
        if (!json?.data?.profile) throw new Error('Player data unavailable');
        if (!cancelled) setData(json.data);
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [playerId, retryKey]);

  const player = useMemo(() => (data ? normalizePlayer(data) : null), [data]);
  const seasonOptions = useMemo(
    () => (player ? sortSeasonsDesc(player.allSeasonStats) : []),
    [player],
  );
  const selectedSeason = useMemo(
    () => (player ? player.allSeasonStats.find((s) => String(s.seasonId) === selectedSeasonId) : null),
    [player, selectedSeasonId],
  );

  useEffect(() => {
    if (!player) { setSelectedSeasonId(null); return; }
    const def = pickDefaultSeason(player.allSeasonStats);
    setSelectedSeasonId(def ? String(def.seasonId) : null);
  }, [player]);

  useEffect(() => {
    const clubId = player?.currentClub?.id;
    if (!clubId) { setClubStandings(null); setStandingsLoading(false); return; }
    let cancelled = false;
    setStandingsLoading(true);
    (async () => {
      try {
        const res = await fetch(TEAM_EP(clubId));
        if (!res.ok) { if (!cancelled) setClubStandings(null); return; }
        const json = await res.json();
        if (!cancelled) setClubStandings(json?.data?.standings || []);
      } catch {
        if (!cancelled) setClubStandings(null);
      } finally {
        if (!cancelled) setStandingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [player?.currentClub?.id]);

  const loadingState = (
    <>
      <SkeletonHero />
      {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
    </>
  );

  const seasonSelectNode = seasonOptions.length > 1 ? (
    <SeasonSelect seasons={seasonOptions} value={selectedSeasonId} onChange={setSelectedSeasonId} />
  ) : null;

  const modal = Boolean(className);

  return (
    <div className={`intel ${className}`.trim()} style={modal ? { padding: '20px 24px 24px' } : undefined}>
      {showBack && <BackButton to="/player-intelligence" label="Back to Player Intelligence" />}
      <div className="mt-4">
        {loading && loadingState}
        {error && <SectionError message={error} onRetry={() => setRetryKey((k) => k + 1)} />}
        {!playerId && !loading && (
          <p className="i-dim text-sm">No player identifier available for this entity.</p>
        )}
        {player && !loading && (
          <>
            <PlayerHero player={player} season={selectedSeason} seasonSelect={seasonSelectNode} />
            <div className="i-card p-0 mt-4 mb-4">
              <TabBar tabs={TABS} active={tab} onChange={setTab} />
              <div className="p-4">
                {tab === 'Overview' && (
                  <div className="grid lg:grid-cols-2 gap-4">
                    <PlayerInfoCard player={player} />
                    <RecentStatsCard player={player} season={selectedSeason} />
                    <HonoursCard player={player} onFull={() => setTab('Career & Trophies')} />
                    <CurrentClubCard player={player} />
                    <TransferHistoryCard player={player} onFull={() => setTab('Career & Trophies')} />
                    <RecentMatchesCard player={player} season={selectedSeason} />
                  </div>
                )}
                {tab === 'Season Stats' && (
                  <div className="grid gap-4">
                    <SeasonStatDetail season={selectedSeason} />
                    <SeasonStatsTable seasons={player.allSeasonStats} highlightId={selectedSeason?.seasonId} />
                  </div>
                )}
                {tab === 'Matches' && <MatchAppearancesTable player={player} season={selectedSeason} />}
                {tab === 'Career & Trophies' && (
                  <div className="grid lg:grid-cols-2 gap-4">
                    <CareerTotalsCard player={player} />
                    <HonoursCard player={player} />
                    <TransferHistoryCard player={player} />
                    <SeasonStatsTable seasons={player.allSeasonStats} />
                  </div>
                )}
                {tab === 'Squad' && (
                  <div className="grid lg:grid-cols-2 gap-4">
                    <CurrentClubCard player={player} />
                    <SquadInfoCard player={player} />
                  </div>
                )}
                {tab === 'Standings' && (() => {
                  const allRows = [...(player?.standings || []), ...(clubStandings || [])];
                  const rows = selectedSeason
                    ? allRows.filter((r) => r?.season_id == null || String(r.season_id) === String(selectedSeason.seasonId))
                    : allRows;
                  if (rows.length) {
                    return (
                      <StandingsTable
                        rows={rows}
                        highlightParticipantId={selectedSeason?.teamId || player.currentClub?.id}
                      />
                    );
                  }
                  return (
                    <p className="i-dim text-sm">
                      {standingsLoading
                        ? 'Loading standings…'
                        : `No standings available for ${selectedSeason ? seasonShortLabel(selectedSeason) : 'this season'}.`}
                    </p>
                  );
                })()}
                {tab === 'Report' && (
                  <PlayerReport
                    data={data}
                    player={player}
                    seasonId={selectedSeason?.seasonId}
                    seasonName={selectedSeason?.seasonName}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
